import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Settings, History, Inbox,
  LayoutDashboard, PlusCircle, ListChecks, ReceiptText, Tags, PiggyBank, Users, UserCircle2, LogOut, Menu, FileBarChart, FileText, FileUp,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { FlagStripe, Logo } from "./Logo";
import { NotificationBell } from "./NotificationBell";
import { FirstOpenPushPrompt } from "@/components/push/FirstOpenPushPrompt";
import { api } from "@/lib/api";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/add", label: "Add Entry", icon: PlusCircle, id: "add-entry" },
  { to: "/transactions", label: "Transactions", icon: ReceiptText, id: "transactions" },
  { to: "/budgets", label: "Budgets", icon: PiggyBank, id: "budgets" },
  { to: "/bills", label: "Bills", icon: FileText, id: "bills" },
  { to: "/review", label: "Pending review", icon: Inbox, id: "review", badge: true },
  { to: "/reports", label: "Reports", icon: FileBarChart, id: "reports" },
  { to: "/import", label: "Import", icon: FileUp, id: "import" },
  { to: "/my-entries", label: "My Entries", icon: ListChecks, id: "my-entries" },
  { to: "/categories", label: "Categories", icon: Tags, id: "categories" },
  { to: "/users", label: "Users", icon: Users, id: "users" },
  { to: "/activity", label: "Activity", icon: History, id: "activity" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
  { to: "/account", label: "Account", icon: UserCircle2, id: "account" },
];
const STAFF_NAV = [
  { to: "/add", label: "Add Entry", icon: PlusCircle, id: "add-entry" },
  { to: "/my-entries", label: "My Entries", icon: ListChecks, id: "my-entries" },
  { to: "/account", label: "Account", icon: UserCircle2, id: "account" },
];

const sideCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-primary-foreground" : "text-foreground/75 hover:bg-secondary hover:text-foreground"
  }`;

const bottomCls = ({ isActive }) =>
  `flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-semibold transition-colors ${
    isActive ? "text-sky-700" : "text-muted-foreground"
  }`;

function usePendingCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/scans/pending/count").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("dls-review-changed", load);
    return () => { clearInterval(t); window.removeEventListener("dls-review-changed", load); };
  }, [enabled]);
  return count;
}

function SideNav({ items, onNavigate, pending = 0 }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="main-navigation">
      {items.map(({ to, label, icon: Icon, id, badge }) => (
        <NavLink key={to} to={to} className={sideCls} onClick={onNavigate} data-testid={`nav-${id}`}>
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
          {label}
          {badge && pending > 0 && <span className="ml-auto min-w-[1.4rem] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold grid place-items-center" data-testid={`nav-${id}-count`}>{pending}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function UserFooter({ user, onLogout }) {
  return (
    <div className="border-t border-border pt-4 mt-4">
      <div className="px-1 mb-3">
        <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
        <div className="text-xs text-muted-foreground truncate">{user.role === "admin" ? "Administrator" : "Standard user"}</div>
      </div>
      <button onClick={onLogout} data-testid="sidebar-logout-button"
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground/75 hover:bg-rose-50 hover:text-rose-700 transition-colors">
        <LogOut className="h-[18px] w-[18px]" /> Log out
      </button>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user.role === "admin";
  const items = isAdmin ? ADMIN_NAV : STAFF_NAV;
  const bottom = isAdmin ? items.slice(0, 4) : items;
  const pending = usePendingCount(isAdmin);
  const doLogout = () => { setOpen(false); logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-card border-r border-border px-4 py-6">
        <FlagStripe className="absolute top-0 left-0" />
        <div className="px-1 mb-6 flex items-start justify-between gap-2"><div data-testid="sidebar-logo-slot"><Logo /></div>{isAdmin && <NotificationBell testId="notification-bell-desktop" />}</div>
        <div className="flex-1 overflow-y-auto"><SideNav items={items} pending={pending} /></div>
        <UserFooter user={user} onLogout={doLogout} />
      </aside>

      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-card/95 backdrop-blur-md border-b border-border px-4 h-16">
        <FlagStripe className="absolute top-0 left-0" />
        <div data-testid="header-logo-slot"><Logo size="sm" tagline={false} /></div>
        {isAdmin && <div className="ml-auto mr-1"><NotificationBell testId="notification-bell" /></div>}
        {isAdmin && (
          <button onClick={() => setOpen(true)} className="relative h-10 w-10 grid place-items-center rounded-xl hover:bg-secondary" data-testid="mobile-menu-button" aria-label="Open menu">
            <Menu className="h-5 w-5" />
            {pending > 0 && <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-rose-500" data-testid="mobile-menu-pending-dot" />}
          </button>
        )}
      </header>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-72 bg-card flex flex-col" data-testid="mobile-menu-sheet">
          <SheetHeader><SheetTitle className="text-left"><Logo /></SheetTitle></SheetHeader>
          <div className="flex-1 mt-6 overflow-y-auto"><SideNav items={items} pending={pending} onNavigate={() => setOpen(false)} /></div>
          <UserFooter user={user} onLogout={doLogout} />
        </SheetContent>
      </Sheet>

      {isAdmin && <FirstOpenPushPrompt />}
      <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-12 max-w-[1400px] mx-auto">{children}</main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex pb-[env(safe-area-inset-bottom)]" data-testid="bottom-navigation">
        {bottom.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} className={bottomCls} data-testid={`bottom-nav-${id}`}>
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
